// The Analytics menu is rendered by the shared top nav on every page, but its items
// call into objects (routeAnalytics, cabinetAnalytics, odAnalytics, ...) that only
// the dashboard constructs - see index.page.js, which builds them from window.route,
// window.segment and window.view. On /v2x/live/, /safety/ and anywhere else that
// includes the nav without those bundles, every item is present, looks clickable and
// silently does nothing.
//
// This turns those dead items into links to the dashboard carrying which item was
// wanted, and replays it on arrival so you land on the same picker you would have got
// by clicking it there.
//
// The intent travels as an element id, NOT as the call string. `?analytic=` is
// attacker-controlled, and the existing hash deep-linking in view.m.js already does
// `eval(anastr + '.run(...)')`; feeding it a value from a query string would be a new
// and much worse hole. An id is only ever used to look up an element inside our own
// nav, so the whitelist is the nav markup itself.
//
// Self-runs at parse time (bottom of file) so a dead item is never painted as live.
// The behaviour is exported so it can be tested without re-importing the module.

export const ANALYTICS_MENU_ID = 'main_menu_analytics';
export const PARAM = 'analytic';
export const DASHBOARD_PATH = '/';

// "routeAnalytics.ttly.run()" -> "routeAnalytics". An item is live when that root
// object exists on window.
export function rootObjectOf(onclick) {
    const match = (/^\s*([A-Za-z_$][\w$]*)/).exec(onclick || '');
    return match ? match[1] : null;
}

export function itemIsLive(el) {
    const root = rootObjectOf(el.getAttribute('onclick'));
    return Boolean(root) && typeof window[root] !== 'undefined';
}

// Whether this page mounts index.page.js, which is what builds routeAnalytics and
// friends. Set from the template (index.html passes analytics_host), NOT probed off
// window.
//
// Probing was the first version of this and it was wrong: the runtime is built
// during index.page.js, which runs after this include parses, so on the dashboard
// itself every item looked dead for a moment and got rewritten. location.m.js then
// reparents those same anchors into the location panel, so clicking "Delay by
// Movement" navigated to /?analytic=... , reloaded the page and dropped the
// selected location instead of running the analytic. A page either has the runtime
// or it does not, and the template knows which - so ask the template.
export function menuIsAnalyticsHost(menu) {
    return menu.getAttribute('data-analytics-host') === '1';
}

// Rewrite the items to dashboard links. Only ever called for a menu that is not on
// the host page, so every item here is dead by construction - there is no runtime
// on this page for any of them to call.
//
// Deliberately no per-item window probe. Which page this is decides the outcome;
// letting an incidentally-present global exempt one item is what made the
// dashboard rewrite itself, and it would leave an item that silently does nothing
// rather than linking home.
export function redirectDeadItems(menu) {
    const items = menu.querySelectorAll('a[onclick]');
    let rewritten = 0;
    for (let i = 0; i < items.length; i++) {
        const el = items[i];
        // Without an id there is nothing to carry, so leave it rather than link to
        // a dashboard that cannot tell what was wanted.
        if (el.id) {
            el.removeAttribute('onclick');
            el.setAttribute('href', DASHBOARD_PATH + '?' + PARAM + '=' + encodeURIComponent(el.id));
            rewritten += 1;
        }
    }
    return rewritten;
}

// On the dashboard, replay the requested item by clicking the real menu entry, so
// there is no second copy of the mapping from item to analytic to drift out of date.
export function replayRequestedItem(menu, search, setTimeoutFn) {
    const requested = new URLSearchParams(search || '').get(PARAM);
    if (!requested) {
        return false;
    }
    let el = null;
    try {
        el = menu.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(requested) : requested));
    } catch (err) {
        // Not a usable selector: ignore it, and never evaluate it.
        return false;
    }
    if (!el || !el.getAttribute('onclick')) {
        return false;
    }
    const later = setTimeoutFn || window.setTimeout.bind(window);
    let attempts = 0;
    const tryClick = function () {
        attempts += 1;
        if (itemIsLive(el)) {
            el.click();
            return;
        }
        // index.page.js constructs routeAnalytics et al. after this include parses,
        // so clicking immediately would hit the undefined this exists to avoid.
        // 40 attempts at 250ms is ~10s, after which it gives up quietly.
        if (attempts < 40) {
            later(tryClick, 250);
        }
    };
    tryClick();
    return true;
}

// On the host page the items are left alone and a ?analytic= request is replayed.
// Everywhere else there is no runtime to wait for, so the items are rewritten.
// The two are exclusive, which is what keeps the dashboard behaving as it always
// has.
export function applyAnalyticsMenuLinks(doc, search, setTimeoutFn) {
    const menu = (doc || document).getElementById(ANALYTICS_MENU_ID);
    if (!menu) {
        return null;
    }
    if (menuIsAnalyticsHost(menu)) {
        return {rewritten: 0, replayed: replayRequestedItem(menu, search, setTimeoutFn)};
    }
    return {rewritten: redirectDeadItems(menu), replayed: false};
}

if (typeof document !== 'undefined' && document.getElementById(ANALYTICS_MENU_ID)) {
    const run = () => applyAnalyticsMenuLinks(document, window.location.search);
    // Rewriting is safe at parse time now that it is decided by the template rather
    // than by whether a runtime happens to exist yet, so a dead item is never
    // painted as clickable. The replay still waits for load, because it needs the
    // analytics objects index.page.js builds.
    if (document.readyState === 'complete') {
        run();
    } else {
        applyAnalyticsMenuLinks(document, '');
        window.addEventListener('load', run);
    }
}
